import React, { useEffect, useState } from 'react';
import {
  Table,
  Button,
  Space,
  Tag,
  Modal,
  Card,
  Row,
  Col,
  Descriptions,
  Typography,
  Select,
  Input,
  Badge,
  Tooltip,
  message,
  Spin,
} from 'antd';
import {
  EyeOutlined,
  EditOutlined,
  FileAddOutlined,
  ReloadOutlined,
  SearchOutlined,
  ToolOutlined,
  DownloadOutlined,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import api from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { exportToCSV } from '../utils/exportUtils';

const { Title, Text } = Typography;
const { Option } = Select;

interface DiagnosticReport {
  id: number;
  intervention_id: number;
  technician_id: number;
  problem_description: string;
  materials_needed?: string;
  recommended_solution: string;
  equipments?: Array<{
    index?: number;
    name?: string;
    type?: string;
    brand?: string;
    location?: string;
    state?: string;
    tested?: boolean;
    before_pression?: string;
    before_puissance?: string;
    before_intensite?: string;
    before_tension?: string;
    before_freon?: string;
    pression?: string;
    puissance?: string;
    intensite?: string;
    tension?: string;
    freon?: string;
  }>;
  pression?: string;
  freon?: string;
  puissance?: string;
  intensite?: string;
  tension?: string;
  parts_needed: Array<{
    name: string;
    quantity: number;
    unit_price?: number;
  }>;
  labor_cost: number;
  estimated_total: number;
  urgency_level: 'low' | 'medium' | 'high' | 'critical';
  estimated_duration: string;
  photos: string[];
  notes: string;
  status: 'submitted' | 'reviewed' | 'quote_sent' | 'approved' | 'rejected';
  submitted_at: string;
  reviewed_by: number | null;
  reviewed_at: string | null;
  created_at: string;
  updated_at: string;
  intervention?: any;
  technician?: any;
  reviewer?: any;
  quotes?: any[];
}

const DiagnosticReportsPage: React.FC = () => {
  const [reports, setReports] = useState<DiagnosticReport[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedReport, setSelectedReport] = useState<DiagnosticReport | null>(null);
  const [detailsModalVisible, setDetailsModalVisible] = useState(false);
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [searchText, setSearchText] = useState('');
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const isAgent = currentUser?.role === 'agent';

  useEffect(() => {
    fetchReports();
  }, [statusFilter]);

  const fetchReports = async () => {
    setLoading(true);
    try {
      const params: any = { limit: 1000 };
      if (statusFilter) params.status = statusFilter;

      const response = await api.get('/diagnostic-reports', { params });

      console.log('API Response:', response.data);
      const reportsData = response.data.reports || [];
      console.log('Reports data:', reportsData);
      setReports(Array.isArray(reportsData) ? reportsData : []);
    } catch (error: any) {
      message.error('Erreur lors du chargement des rapports');
      console.error('Error fetching reports:', error);
    } finally {
      setLoading(false);
    }
  };

  const viewReportDetails = async (reportId: number) => {
    try {
      const response = await api.get(`/diagnostic-reports/${reportId}`);

      setSelectedReport(response.data);
      setDetailsModalVisible(true);
    } catch (error: any) {
      message.error('Erreur lors du chargement des détails');
      console.error('Error fetching report details:', error);
    }
  };

  const handleCreateQuote = (reportId: number) => {
    navigate(`/quotes/create-from-report/${reportId}`);
  };

  const handleUpdateStatus = async (reportId: number, newStatus: string) => {
    try {
      await api.patch(`/diagnostic-reports/${reportId}/status`, { status: newStatus });

      message.success('Statut mis à jour avec succès');
      fetchReports();
      if (selectedReport && selectedReport.id === reportId) {
        setDetailsModalVisible(false);
      }
    } catch (error: any) {
      message.error('Erreur lors de la mise à jour du statut');
      console.error('Error updating status:', error);
    }
  };

  const getUrgencyColor = (level: string) => {
    switch (level) {
      case 'low':
        return 'green';
      case 'medium':
        return 'orange';
      case 'high':
        return 'red';
      case 'critical':
        return 'purple';
      default:
        return 'default';
    }
  };

  const getUrgencyLabel = (level: string) => {
    switch (level) {
      case 'low':
        return 'Faible';
      case 'medium':
        return 'Moyen';
      case 'high':
        return 'Élevé';
      case 'critical':
        return 'Critique';
      default:
        return level;
    }
  };

  const getStatusBadge = (status: string) => {
    const statusConfig: Record<string, { status: any; text: string }> = {
      submitted: { status: 'processing', text: 'Soumis' },
      reviewed: { status: 'warning', text: 'Examiné' },
      quote_sent: { status: 'default', text: 'Devis envoyé' },
      approved: { status: 'success', text: 'Approuvé' },
      rejected: { status: 'error', text: 'Rejeté' },
    };

    const config = statusConfig[status] || { status: 'default', text: status };
    return <Badge status={config.status} text={config.text} />;
  };

  const filteredReports = Array.isArray(reports) 
    ? reports.filter(
        (report) =>
          report.problem_description?.toLowerCase().includes(searchText.toLowerCase()) ||
          report.id?.toString().includes(searchText) ||
          report.intervention_id?.toString().includes(searchText)
      )
    : [];

  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 70,
      sorter: (a: DiagnosticReport, b: DiagnosticReport) => a.id - b.id,
    },
    {
      title: 'Intervention',
      dataIndex: 'intervention_id',
      key: 'intervention_id',
      width: 100,
      render: (id: number) => `#${id}`,
    },
    {
      title: 'Technicien',
      key: 'technician',
      width: 150,
      render: (record: DiagnosticReport) =>
        record.technician
          ? `${record.technician.first_name || ''} ${record.technician.last_name || ''}`.trim() || 'N/A'
          : 'N/A',
    },
    {
      title: 'Problème',
      dataIndex: 'problem_description',
      key: 'problem_description',
      ellipsis: {
        showTitle: false,
      },
      render: (text: string) => (
        <Tooltip title={text}>
          <span>{text}</span>
        </Tooltip>
      ),
    },
    {
      title: 'Total estimé',
      dataIndex: 'estimated_total',
      key: 'estimated_total',
      width: 120,
      render: (amount: number) => 
        Number(amount) > 0 ? `${Number(amount).toFixed(0)} FCFA` : <Text type="secondary">À estimer</Text>,
      sorter: (a: DiagnosticReport, b: DiagnosticReport) =>
        (a.estimated_total || 0) - (b.estimated_total || 0),
    },
    {
      title: 'Urgence',
      dataIndex: 'urgency_level',
      key: 'urgency_level',
      width: 100,
      render: (level: string) => (
        <Tag color={getUrgencyColor(level)}>{getUrgencyLabel(level)}</Tag>
      ),
      filters: [
        { text: 'Faible', value: 'low' },
        { text: 'Moyen', value: 'medium' },
        { text: 'Élevé', value: 'high' },
        { text: 'Critique', value: 'critical' },
      ],
      onFilter: (value: any, record: DiagnosticReport) =>
        record.urgency_level === value,
    },
    {
      title: 'Statut',
      dataIndex: 'status',
      key: 'status',
      width: 130,
      render: (status: string) => getStatusBadge(status),
    },
    {
      title: 'Date',
      dataIndex: 'submitted_at',
      key: 'submitted_at',
      width: 110,
      render: (date: string) => dayjs(date).format('DD/MM/YYYY'),
      sorter: (a: DiagnosticReport, b: DiagnosticReport) =>
        dayjs(a.submitted_at).unix() - dayjs(b.submitted_at).unix(),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 180,
      render: (record: DiagnosticReport) => (
        <Space>
          <Tooltip title="Voir détails">
            <Button
              type="link"
              icon={<EyeOutlined />}
              onClick={() => viewReportDetails(record.id)}
            />
          </Tooltip>
          {!isAgent && record.status === 'submitted' && (
            <Tooltip title="Créer un devis">
              <Button
                type="primary"
                size="small"
                icon={<FileAddOutlined />}
                onClick={() => handleCreateQuote(record.id)}
              >
                Devis
              </Button>
            </Tooltip>
          )}
        </Space>
      ),
    },
  ];

  const [exporting, setExporting] = useState<boolean>(false);

  const handleExport = () => {
    try {
      setExporting(true);
      const rows = (Array.isArray(filteredReports) ? filteredReports : []).map(r => ({
        id: r.id,
        intervention_id: r.intervention_id,
        technicien: r.technician ? `${r.technician.first_name || ''} ${r.technician.last_name || ''}`.trim() : 'N/A',
        probleme: r.problem_description || '',
        solution: r.recommended_solution || '',
        urgence: getUrgencyLabel(r.urgency_level),
        total_estime: isAgent ? '' : (r.estimated_total ? `${r.estimated_total} FCFA` : 'À estimer'),
        statut: r.status,
        date_soumission: r.submitted_at ? dayjs(r.submitted_at).format('DD/MM/YYYY HH:mm') : ''
      }));

      const headers = [
        { header: 'ID', key: 'id' },
        { header: 'N° Intervention', key: 'intervention_id' },
        { header: 'Technicien', key: 'technicien' },
        { header: 'Description Problème', key: 'probleme' },
        { header: 'Solution Recommandée', key: 'solution' },
        { header: 'Niveau Urgence', key: 'urgence' },
        ...(!isAgent ? [{ header: 'Total Estimé', key: 'total_estime' }] : []),
        { header: 'Statut', key: 'statut' },
        { header: 'Date de Soumission', key: 'date_soumission' },
      ];

      exportToCSV(rows, headers, `rapports_diagnostics_${new Date().toISOString().slice(0, 10)}.csv`);
      message.success('Export des rapports de diagnostic généré avec succès');
    } catch (err) {
      console.error('Erreur export diagnostics:', err);
      message.error("Erreur lors de l'export des diagnostics");
    } finally {
      setExporting(false);
    }
  };

  return (
    <div style={{ padding: '24px' }}>
      <Card>
        <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
          <Col>
            <Title level={3}>Rapports de Diagnostic</Title>
          </Col>
          <Col>
            <Space>
              <Button
                icon={<DownloadOutlined />}
                onClick={handleExport}
                loading={exporting}
              >
                Exporter
              </Button>
              <Button
                type="primary"
                icon={<ReloadOutlined />}
                onClick={fetchReports}
                loading={loading}
              >
                Actualiser
              </Button>
            </Space>
          </Col>
        </Row>

        <Row gutter={16} style={{ marginBottom: 16 }}>
          <Col span={8}>
            <Input
              placeholder="Rechercher par ID, intervention, problème..."
              prefix={<SearchOutlined />}
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              allowClear
            />
          </Col>
          <Col span={8}>
            <Select
              placeholder="Filtrer par statut"
              style={{ width: '100%' }}
              value={statusFilter || undefined}
              onChange={(value) => setStatusFilter(value || '')}
              allowClear
            >
              <Option value="submitted">Soumis</Option>
              <Option value="reviewed">Examiné</Option>
              <Option value="quote_sent">Devis envoyé</Option>
              <Option value="approved">Approuvé</Option>
              <Option value="rejected">Rejeté</Option>
            </Select>
          </Col>
        </Row>

        <Table
          columns={columns}
          dataSource={Array.isArray(filteredReports) ? filteredReports : []}
          rowKey="id"
          loading={loading}
          pagination={{
            pageSize: 20,
            showTotal: (total) => `Total: ${total} rapports`,
            showSizeChanger: true,
          }}
        />
      </Card>

      {/* Details Modal */}
      <Modal
        title={`Rapport de Diagnostic #${selectedReport?.id}`}
        open={detailsModalVisible}
        onCancel={() => setDetailsModalVisible(false)}
        width={900}
        footer={[
          <Button key="close" onClick={() => setDetailsModalVisible(false)}>
            Fermer
          </Button>,
          !isAgent && selectedReport?.status === 'submitted' && (
            <Button
              key="quote"
              type="primary"
              icon={<FileAddOutlined />}
              onClick={() => {
                if (selectedReport) {
                  handleCreateQuote(selectedReport.id);
                }
              }}
            >
              Créer un devis
            </Button>
          ),
        ]}
      >
        {selectedReport && (
          <div>
            <Descriptions bordered column={2} size="small">
              <Descriptions.Item label="Intervention">
                #{selectedReport.intervention_id}
              </Descriptions.Item>
              <Descriptions.Item label="Statut">
                {getStatusBadge(selectedReport.status)}
              </Descriptions.Item>
              <Descriptions.Item label="Technicien">
                {selectedReport.technician
                  ? `${selectedReport.technician.first_name || ''} ${selectedReport.technician.last_name || ''}`.trim() || 'N/A'
                  : 'N/A'}
              </Descriptions.Item>
              <Descriptions.Item label="Urgence">
                <Tag color={getUrgencyColor(selectedReport.urgency_level)}>
                  {getUrgencyLabel(selectedReport.urgency_level)}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label="Date de soumission" span={2}>
                {dayjs(selectedReport.submitted_at).format('DD/MM/YYYY HH:mm')}
              </Descriptions.Item>
              <Descriptions.Item label="Durée estimée" span={2}>
                {selectedReport.estimated_duration}
              </Descriptions.Item>
            </Descriptions>

            {/* Section Équipements & Données Techniques */}
            {(() => {
              let equipmentsList: any[] = [];
              if (selectedReport.equipments) {
                if (typeof selectedReport.equipments === 'string') {
                  try {
                    equipmentsList = JSON.parse(selectedReport.equipments);
                  } catch (_) {
                    equipmentsList = [];
                  }
                } else if (Array.isArray(selectedReport.equipments)) {
                  equipmentsList = selectedReport.equipments;
                }
              }

              if (equipmentsList.length === 0 &&
                  (selectedReport.pression || selectedReport.freon || selectedReport.puissance || selectedReport.intensite || selectedReport.tension || (selectedReport as any).equipment_brand || (selectedReport as any).equipment_type)) {
                equipmentsList = [{
                  index: 1,
                  type: (selectedReport as any).equipment_type || 'Mural',
                  brand: (selectedReport as any).equipment_brand || '',
                  location: (selectedReport as any).location || '',
                  state: (selectedReport as any).equipment_state || '',
                  tested: (selectedReport as any).tested,
                  before_pression: selectedReport.pression,
                  before_puissance: selectedReport.puissance,
                  before_intensite: selectedReport.intensite,
                  before_tension: selectedReport.tension,
                  before_freon: selectedReport.freon,
                }];
              }

              if (equipmentsList.length === 0) return null;

              return (
                <div style={{ marginTop: '16px' }}>
                  <Title level={5}>🔧 Équipements & Constantes Avant Intervention</Title>
                  {equipmentsList.map((equipment: any, idx: number) => {
                    const bPression = equipment.before_pression || equipment.pression || '';
                    const bPuissance = equipment.before_puissance || equipment.puissance || '';
                    const bIntensite = equipment.before_intensite || equipment.intensite || '';
                    const bTension = equipment.before_tension || equipment.tension || '';
                    const bFreon = equipment.before_freon || equipment.freon || '';
                    const hasMeasures = bPression || bPuissance || bIntensite || bTension || bFreon;

                    return (
                      <Card key={idx} size="small" style={{ marginBottom: '12px', background: '#f6ffed', border: '1px solid #b7eb8f' }}>
                        <Row gutter={[16, 8]}>
                          <Col span={24}>
                            <Text strong style={{ fontSize: '14px', color: '#0a543d' }}>
                              #{equipment.index || idx + 1} {equipment.name || `${equipment.brand || 'Équipement'} ${equipment.type ? `- ${equipment.type}` : ''}`}
                            </Text>
                          </Col>
                          {equipment.type && (
                            <Col span={8}>
                              <Text type="secondary" style={{ fontSize: '12px' }}>Type: </Text>
                              <Text strong>{equipment.type}</Text>
                            </Col>
                          )}
                          {equipment.brand && (
                            <Col span={8}>
                              <Text type="secondary" style={{ fontSize: '12px' }}>Marque: </Text>
                              <Text strong>{equipment.brand}</Text>
                            </Col>
                          )}
                          {equipment.location && (
                            <Col span={8}>
                              <Text type="secondary" style={{ fontSize: '12px' }}>Emplacement: </Text>
                              <Text strong>{equipment.location}</Text>
                            </Col>
                          )}
                          {equipment.state && (
                            <Col span={8}>
                              <Text type="secondary" style={{ fontSize: '12px' }}>État: </Text>
                              <Text strong>{equipment.state}</Text>
                            </Col>
                          )}
                          {equipment.tested !== undefined && (
                            <Col span={8}>
                              <Text type="secondary" style={{ fontSize: '12px' }}>Test: </Text>
                              <Tag color={equipment.tested ? 'success' : 'error'}>
                                {equipment.tested ? 'Oui' : 'Non'}
                              </Tag>
                            </Col>
                          )}
                        </Row>

                        {hasMeasures && (
                          <div style={{ marginTop: '12px', paddingTop: '8px', borderTop: '1px dashed #b7eb8f' }}>
                            <Text strong style={{ fontSize: '12px', color: '#0a543d' }}>
                              🟢 Données Techniques (Constantes avant intervention)
                            </Text>
                            <Row gutter={[12, 6]} style={{ marginTop: '6px' }}>
                              {bIntensite && <Col span={8}><Text type="secondary">Intensité: </Text><Text strong>{bIntensite} A</Text></Col>}
                              {bTension && <Col span={8}><Text type="secondary">Tension: </Text><Text strong>{bTension} V</Text></Col>}
                              {bFreon && <Col span={8}><Text type="secondary">Fréon: </Text><Text strong>{bFreon}</Text></Col>}
                              {bPression && <Col span={8}><Text type="secondary">Pression: </Text><Text strong>{bPression} bar</Text></Col>}
                              {bPuissance && <Col span={8}><Text type="secondary">Puissance: </Text><Text strong>{bPuissance} CV</Text></Col>}
                            </Row>
                          </div>
                        )}
                      </Card>
                    );
                  })}
                </div>
              );
            })()}

            {/* Point 6: Décrire la panne */}
            <Card title="📌 6/ Description / Constat de la Panne" style={{ marginTop: 16 }} size="small">
              <Text style={{ whiteSpace: 'pre-wrap' }}>{selectedReport.problem_description || 'Non spécifié'}</Text>
            </Card>

            {/* Point 7: Matériels nécessaires */}
            {(() => {
              let partsList: any[] = [];
              if (selectedReport.parts_needed) {
                if (typeof selectedReport.parts_needed === 'string') {
                  try {
                    partsList = JSON.parse(selectedReport.parts_needed);
                  } catch (_) {
                    partsList = [];
                  }
                } else if (Array.isArray(selectedReport.parts_needed)) {
                  partsList = selectedReport.parts_needed;
                }
              }

              if (!selectedReport.materials_needed && partsList.length === 0) return null;

              return (
                <Card title="🛠️ 7/ Matériels Nécessaires (Dépannage / Installation)" style={{ marginTop: 16 }} size="small">
                  {selectedReport.materials_needed && (
                    <div style={{ marginBottom: partsList.length > 0 ? 12 : 0 }}>
                      <Text style={{ whiteSpace: 'pre-wrap' }}>{selectedReport.materials_needed}</Text>
                    </div>
                  )}
                  {partsList.length > 0 && (
                    <Table
                      dataSource={partsList}
                      columns={[
                        { title: 'Pièce', dataIndex: 'name', key: 'name' },
                        {
                          title: 'Quantité',
                          dataIndex: 'quantity',
                          key: 'quantity',
                          width: 100,
                        },
                        {
                          title: 'Prix unitaire',
                          dataIndex: 'unit_price',
                          key: 'unit_price',
                          width: 120,
                          render: (price: number) => (price ? `${Number(price || 0).toFixed(0)} FCFA` : '-'),
                        },
                      ]}
                      pagination={false}
                      size="small"
                    />
                  )}
                </Card>
              );
            })()}

            <Card style={{ marginTop: 16 }} size="small">
              <Row gutter={16}>
                <Col span={12}>
                  <Text strong>Coût main d'œuvre: </Text>
                  <Text>{Number(selectedReport.labor_cost || 0).toFixed(0)} FCFA</Text>
                </Col>
                <Col span={12}>
                  <Text strong>Total estimé: </Text>
                  <Text style={{ fontSize: 18, color: '#1890ff', fontWeight: 'bold' }}>
                    {Number(selectedReport.estimated_total || 0).toFixed(0)} FCFA
                  </Text>
                </Col>
              </Row>
            </Card>

            {selectedReport.notes && (
              <Card title="Notes" style={{ marginTop: 16 }} size="small">
                <Text>{selectedReport.notes}</Text>
              </Card>
            )}

            {/* Travaux Réalisés Après Intervention (si rapport soumis) */}
            {(() => {
              let reportData = null;
              if (selectedReport.intervention?.report_data) {
                reportData = typeof selectedReport.intervention.report_data === 'string'
                  ? JSON.parse(selectedReport.intervention.report_data)
                  : selectedReport.intervention.report_data;
              }

              if (!reportData && !(selectedReport as any).after_intervention_report) return null;

              return (
                <Card 
                  title={<Space><ToolOutlined style={{ color: '#52c41a' }} /><span style={{ color: '#0a543d', fontWeight: 'bold' }}>✅ Travaux Réalisés (Rapport de Fin d'Intervention)</span></Space>}
                  style={{ marginTop: 16, border: '1px solid #b7eb8f' }}
                  headStyle={{ background: '#f6ffed' }}
                  size="small"
                >
                  {reportData ? (
                    <div>
                      {reportData.intervention_nature && (
                        <div style={{ marginBottom: 8 }}>
                          <Text strong style={{ color: '#0a543d' }}>Nature des travaux : </Text>
                          <Tag color="cyan">{reportData.intervention_nature}</Tag>
                        </div>
                      )}
                      {reportData.work_description && (
                        <div style={{ marginBottom: 8 }}>
                          <Text strong>Description des travaux : </Text>
                          <div style={{ padding: '8px', background: '#fff', borderRadius: '4px', border: '1px solid #d9d9d9', marginTop: '4px', whiteSpace: 'pre-wrap' }}>
                            {reportData.work_description}
                          </div>
                        </div>
                      )}
                      {reportData.conclusion_installation && (
                        <div style={{ marginBottom: 8 }}>
                          <Text strong style={{ color: '#0a543d' }}>🎯 Conclusion de l'installation (Typologie) : </Text>
                          <div style={{ padding: '8px', background: '#f6ffed', borderRadius: '4px', border: '1px solid #b7eb8f', marginTop: '4px', whiteSpace: 'pre-wrap' }}>
                            {reportData.conclusion_installation}
                          </div>
                        </div>
                      )}
                      {reportData.observations && (
                        <div style={{ marginBottom: 8 }}>
                          <Text strong style={{ color: '#cf7a00' }}>Observations : </Text>
                          <div style={{ padding: '8px', background: '#fff7e6', borderRadius: '4px', border: '1px solid #ffd591', marginTop: '4px', whiteSpace: 'pre-wrap' }}>
                            {reportData.observations}
                          </div>
                        </div>
                      )}
                      {(reportData.spare_parts?.length > 0 || reportData.materials_used?.length > 0) && (
                        <div style={{ marginTop: 8 }}>
                          <Text strong>Pièces & Matériels utilisés : </Text>
                          <ul style={{ margin: '4px 0 0 0', paddingLeft: '20px' }}>
                            {(reportData.spare_parts || reportData.materials_used || []).map((p: any, idx: number) => (
                              <li key={idx}><strong>{p.name}</strong> {p.quantity ? `(x${p.quantity})` : ''}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  ) : (
                    <Text style={{ whiteSpace: 'pre-wrap' }}>{(selectedReport as any).after_intervention_report}</Text>
                  )}
                </Card>
              );
            })()}

            {selectedReport.reviewed_by && selectedReport.reviewed_at && (
              <Card title="Examen" style={{ marginTop: 16 }} size="small">
                <Text>
                  Examiné le {dayjs(selectedReport.reviewed_at).format('DD/MM/YYYY HH:mm')}
                  {selectedReport.reviewer &&
                    ` par ${selectedReport.reviewer.first_name || ''} ${selectedReport.reviewer.last_name || ''}`.trim()}
                </Text>
              </Card>
            )}
          </div>
        )}
      </Modal>
    </div>
  );
};

export default DiagnosticReportsPage;
